// @ts-check

/**
 * Navigation item
 * @typedef {Object} NavItem
 * @property {number} id - Unique identifier
 * @property {HTMLButtonElement} button - Navigation button that toggles the subnav
 * @property {HTMLUListElement | null} listElement - Sub navigation list element
 * @property {NavItems | null} subnav - Sub navigation panel
 * @property {HTMLElement} parent - Parent element
 * @property {boolean} isOpen - Is the subnav open
 */
/**
 * Navigation items
 * @typedef {Array<NavItem>} NavItems
 */

/**
 * @param {HTMLCollection | undefined} links
 * @returns {NavItems}
 */
const buildNavItems = (links) => {
    if (!links) {
        return [];
    }

    return Array.from(links)
        .map((item, index) => {
            const button = item.children.item(0);
            const subnav = item.children.item(1);

            if (
                button instanceof HTMLButtonElement &&
                subnav instanceof HTMLUListElement
            ) {
                return {
                    id: index,
                    button,
                    listElement: subnav,
                    subnav: buildNavItems(subnav.children),
                    parent: item,
                    isOpen: false,
                };
            }

            return {
                id: index,
                button,
                subnav: null,
                parent: item,
                isOpen: false,
            };
        })
        .filter((item) => item !== undefined);
};

/**
 * nav
 */
class MobileNavigation {
    /**
     * @param {HTMLElement} nav - Mobile navigation panel "Root" element
     */
    constructor(nav) {
        this.nav = nav;
        /**
         * @type {HTMLBodyElement | null}
         */
        this.documentBody = document.querySelector("body");
        /**
         * @type {HTMLElement | null}
         */
        this.navTrigger = document.getElementById("mobile-navigation-trigger");

        this.isNavOpen = this.nav.getAttribute("data-menu-open") === "true";

        const links = this.#getNavLinks("data-mobile-menu-links");

        this.navLinks = buildNavItems(links);

        // Set initial state
        this.closeNav();

        this.navTrigger?.addEventListener("click", () => this.toggleNav());

        this.#handleSubNavClick(this.navLinks);
    }

    /**
     * @param {KeyboardEvent} event
     * Keyboard event handler for closing the navigation panel
     * @returns {void}
     */
    #keyboardESCHandler(event) {
        if (event.key === "Escape" || event.keyCode === 27) {
            this.closeNav();
        }
    }

    closeNav() {
        if (!this.nav || !this.documentBody) {
            console.error("No navigation panel found");
            return;
        }

        this.nav.setAttribute("data-menu-open", "false");
        this.isNavOpen = false;
        this.navTrigger?.setAttribute("aria-expanded", "false");

        this.nav.classList.remove("flex");
        this.nav.classList.add("hidden");

        this.documentBody.classList.remove("overflow-hidden");
        window.removeEventListener("keydown", this.#keyboardESCHandler.bind(this));

        if (this.resizeHandler) {
            window.removeEventListener("resize", this.resizeHandler);
        }

        // Focus the navigation trigger - accessibility
        this.navTrigger?.focus();
    }

    openNav() {
        if (!this.nav || !this.documentBody) {
            console.error("No navigation panel found");
            return;
        }

        this.nav.setAttribute("data-menu-open", "true");
        this.isNavOpen = true;
        this.navTrigger?.setAttribute("aria-expanded", "true");

        this.nav.classList.add("flex");
        this.nav.classList.remove("hidden");

        this.#setMobileNavHeight();

        this.resizeHandler = () => {
            this.#setMobileNavHeight();
        };

        window.addEventListener("resize", this.resizeHandler);

        this.documentBody.classList.add("overflow-hidden");
        window.addEventListener("keydown", this.#keyboardESCHandler.bind(this));
    }

    toggleNav() {
        this.isNavOpen ? this.closeNav() : this.openNav();
    }

    #setMobileNavHeight() {
        // Set mobile navigation height to full height minus offset from top (e.g. top bars)
        document &&
            document
                ?.getElementById("mobile-navigation-panel")
                ?.setAttribute(
                    "style",
                    `height: ${window.innerHeight - this.nav.getBoundingClientRect().top
                    }px`
                );
    }

    /**
     * @param {string} target
     * @returns {HTMLCollection | undefined}
     */
    #getNavLinks(target) {
        const element = document.getElementById(target);
        if (!element) {
            //console.error("No target element found", target);
            return;
        }

        return element.children;
    }

    /**
     * @param {NavItems} navItems
     * @param {HTMLButtonElement | null} parent
     * @returns { void }
     */
    #handleSubNavClick(navItems, parent = null) {
        navItems.map((item) => {
            if (!item.subnav) return;

            item.button.addEventListener("click", () => {
                if (item.isOpen) {
                    item.isOpen = false;

                    item.listElement?.classList.add("hidden");
                    item.listElement?.classList.remove("flex");
                    item.button.setAttribute("aria-expanded", "false");

                    if (parent) {
                        parent.classList.remove("hidden");
                    }

                    navItems.map((navItem) => {
                        if (navItem.id !== item.id) {
                            navItem.parent.classList.remove("hidden");
                            navItem.button.setAttribute("aria-expanded", "false");
                        }
                    });
                } else {
                    item.isOpen = true;

                    item.listElement?.classList.remove("hidden");
                    item.listElement?.classList.add("flex");
                    item.button.setAttribute("aria-expanded", "true");

                    if (parent) {
                        parent.classList.add("hidden");
                    }

                    navItems.map((navItem) => {
                        if (navItem.id !== item.id) {
                            navItem.parent.classList.add("hidden");
                            navItem.button.setAttribute("aria-expanded", "false");
                        }
                    });
                }
            });

            this.#handleSubNavClick(item.subnav, item.button);
        });
    }
}

const mobileNav = document.getElementById("mobile-navigation-panel");

if (mobileNav) {
    const mobileNavClass = new MobileNavigation(mobileNav);

    // Current orientation width
    let initialWidth = window.innerWidth;

    window.addEventListener("resize", () => {
        // Width has changed -> orientation change
        if (window.innerWidth !== initialWidth) {
            // Close the navigation
            if (mobileNavClass.isNavOpen) {
              mobileNavClass.closeNav();
            }

            // Update curent orientation width
            initialWidth = window.innerWidth; 
        } 
    });
}

// Navigation
const navItems = document.getElementById("navigation-items");
/** @type {HTMLButtonElement | null} */
const topbarButton = document.querySelector("button#topbar-login-button");
/** @type {HTMLDivElement | null} */
const topbarContainer = document.querySelector("div#topbar-container");

/**
 * @typedef {Object} RootNavItem
 * @property {number} id
 * @property {HTMLButtonElement} button
 * @property {HTMLElement|null} subnav
 * @property {boolean} isOpen
 */

/**
 * @typedef {Object} Navigation
 * @property {HTMLElement} root
 * @property {RootNavItem[]} items
 * @property {RootNavItem|null} currentItem
 * @property {boolean} isOpen
 * @property {Function} open
 * @property {Function} close
 * @property {HTMLDivElement[]} backdrops
 */

/**
 * Utility to show/hide element by class names
 * @param {HTMLElement | null} el - Target element
 * @param {boolean} show - Show or hide
 * @param {string} showClass - Class to add when showing
 * @param {string} hideClass - Class to add when hiding
 */
const setVisibility = (
    el,
    show,
    showClass = "lg:block",
    hideClass = "lg:hidden"
) => {
    if (!el) return;
    el.classList.toggle(showClass, show);
    el.classList.toggle(hideClass, !show);
    el.setAttribute("aria-hidden", (!show).toString());
};

/**
 * Opens a navigation item
 * @param {RootNavItem} item
 * @param {Navigation} nav
 * @returns {void}
 */
function openNavItem(item, nav) {
    if (!nav.isOpen) nav.open();
    if (nav.currentItem?.id !== item.id && nav.currentItem)
        closeNavItem(nav.currentItem, nav);
    nav.currentItem = item;
    item.button.setAttribute("aria-expanded", "true");
    item.isOpen = true;

    if (item.subnav) {
        // Read class names from button data attributes, fallback to "lg:block" and "lg:hidden"
        const showClass = item.button.getAttribute("data-show-class") || "lg:block";
        const hideClass =
            item.button.getAttribute("data-hide-class") || "lg:hidden";
        setVisibility(item.subnav, true, showClass, hideClass);
        item.subnav.focus();
    }
}

/**
 * Closes a navigation item
 * @param {RootNavItem} item
 * @param {Navigation} nav
 * @returns {void}
 */
function closeNavItem(item, nav) {
    item.button.setAttribute("aria-expanded", "false");
    item.isOpen = false;
    if (nav.currentItem === item) nav.currentItem = null;
    if (item.subnav) {
        const showClass = item.button.getAttribute("data-show-class") || "lg:block";
        const hideClass =
            item.button.getAttribute("data-hide-class") || "lg:hidden";
        setVisibility(item.subnav, false, showClass, hideClass);
    }
}

/**
 * Toggles a navigation item
 * @param {RootNavItem} item
 * @param {Navigation} nav
 * @returns {void}
 */
function toggleNavItem(item, nav) {
    item.isOpen ? (closeNavItem(item, nav), nav.close()) : openNavItem(item, nav);
}

/**
 * Handles third-level submenu buttons
 * @param {HTMLButtonElement[]} buttons - Array of third-level menu buttons
 * @param {HTMLElement} parent - Parent element containing all subnavs
 * @returns {void}
 */
function handleThirdLevelButtons(buttons, parent) {
    /** @type {Record<string, HTMLDivElement>} */
    const subnavs = Object.fromEntries(
        Array.from(parent.querySelectorAll("[data-nav-third-level='true']"))
            .filter((subnav) => subnav.id)
            .map((subnav) => [subnav.id, subnav])
    );

    /** @type {string | null} */
    let openItem = null;

    buttons.forEach((button) => {
        button.setAttribute(
            "aria-expanded",
            (button.getAttribute("aria-expanded") === "true").toString()
        );

        button.addEventListener("click", () => {
            /** @type {string | null} */
            const targetId = button.getAttribute("data-nav-third-level-button");
            if (!targetId) return;

            // Close previously open subnav
            if (openItem && openItem !== targetId) {
                const prevSubnav = subnavs[openItem];
                const prevButton = parent.querySelector(
                    `button[data-nav-third-level-button='${openItem}']`
                );
                if (prevSubnav) setVisibility(prevSubnav, false);
                if (prevButton) prevButton.setAttribute("aria-expanded", "false");
            }

            // Toggle target subnav
            const targetSubnav = subnavs[targetId];
            if (!targetSubnav) return;
            const isCurrentlyOpen = button.getAttribute("aria-expanded") === "true";

            setVisibility(targetSubnav, !isCurrentlyOpen);
            button.setAttribute("aria-expanded", (!isCurrentlyOpen).toString());

            openItem = targetId;
        });
    });
}

/**
 * Creates a navigation object for the specified target
 * @param {string} targetId
 * @returns {Navigation | null}
 */
function createNavigation(targetId) {
    const root = document.getElementById(targetId);

    /** @type {Array<HTMLDivElement>} */
    const backdrops = Array.from(
        document.querySelectorAll('[data-element-type="main-menu-backdrop"]')
    );

    if (!root || backdrops.length === 0) {
        console.error("No target element found", targetId);
        return null;
    }

    let isOpen = root.getAttribute("data-nav-open") === "true";

    /** Open navigation */
    const open = () => {
        root.setAttribute("data-nav-open", "true");
        isOpen = true;
        backdrops.forEach((bd) => setVisibility(bd, true));
    };

    /** Close navigation */
    const close = () => {
        root.setAttribute("data-nav-open", "false");
        isOpen = false;
        backdrops.forEach((bd) => setVisibility(bd, false));
    };

    /**
     * Build navigation items from DOM buttons
     * @returns {RootNavItem[]}
     */
    const buildNavItems = () => {
        /** @type {Array<HTMLButtonElement>} */
        const buttons = Array.from(
            root.querySelectorAll("button[data-nav='root-item']")
        );

        return buttons.map((button, id) => {
            const targetId = button.getAttribute("data-nav-target");
            const subnav = targetId ? document.getElementById(targetId) : null;

            if (subnav) {
                /** @type {Array<HTMLButtonElement>} */
                const thirdButtons = Array.from(
                    subnav.querySelectorAll("[data-nav-third-level-button]")
                );

                if (thirdButtons.length) handleThirdLevelButtons(thirdButtons, root);
            }
            return { id, button, subnav, isOpen };
        });
    };

    const navObj = {
        root,
        items: buildNavItems(),
        isOpen,
        currentItem: null,
        backdrops,
        open,
        close,
    };
    return navObj;
}

document.addEventListener("DOMContentLoaded", () => {
    const navElements = document.querySelectorAll("[data-element-type='nav']");

    navElements.forEach((el) => {
        const nav = createNavigation(el.id);

        if (!nav) return;

        nav.items.forEach((item) =>
            item.button.addEventListener("click", () => toggleNavItem(item, nav))
        );

        /** @type {HTMLButtonElement | null} */
        const navSearchButton = nav.root.querySelector(
            "[data-nav='search-button']"
        );

        if (navSearchButton) {
            const search = createSearch(navSearchButton, nav);

            if (!search) {
                console.error("No search created");
            }
        }
        window.addEventListener("resize", () => {
            // Close navigation on desktop resize
            if (nav.currentItem) closeNavItem(nav.currentItem, nav);
            nav.close();
        });

        // Backdrop events: close on click/Escape
        nav.backdrops.forEach((bd) => {
            setVisibility(bd, false);
            bd.addEventListener("click", () => {
                if (nav.currentItem) closeNavItem(nav.currentItem, nav);
                nav.close();
            });
            window.addEventListener("keydown", (e) => {
                if (e.key === "Escape" || e.keyCode === 27) {
                    if (nav.currentItem) closeNavItem(nav.currentItem, nav);
                    nav.close();
                }
            });
        });
    });
});

/**
 * Search functionality
 * @param {HTMLButtonElement} searchButton
 * @param {Navigation} nav
 */
const createSearch = (searchButton, nav) => {
    const searchContainer = nav.root.querySelector(
        "[data-nav='search-container']"
    );
    const closeButton = nav.root.querySelector(
        "[data-nav='search-close-button']"
    );
    const navItems = nav.root.querySelector("[data-nav='nav-items']");

    if (!searchContainer || !closeButton) {
        console.error("No search target found for button", searchButton);
        return null;
    }

    const searchButtonShowClass =
        searchButton.getAttribute("data-show-class") || "lg:flex";
    const searchButtonHideClass =
        searchButton.getAttribute("data-hide-class") || "hidden";

    const searchShowClass =
        searchContainer.getAttribute("data-show-class") || "lg:flex";
    const searchHideClass =
        searchContainer.getAttribute("data-hide-class") || "hidden";
    const navShowClass = navItems?.getAttribute("data-show-class") || "lg:flex";
    const navHideClass = navItems?.getAttribute("data-hide-class") || "hidden";

    let isSearchOpen = false;

    /**
     * @param {KeyboardEvent} e
     */
    const handleKeydown = (e) => {
        if (e.key === "Escape" || e.keyCode === 27) {
            closeSearch();
        }
    };

    const openSearch = () => {
        isSearchOpen = true;

        searchContainer.classList.remove(searchHideClass);
        searchContainer.classList.add(searchShowClass);
        searchContainer.setAttribute("aria-hidden", "false");

        searchButton.classList.add(searchButtonHideClass);
        searchButton.classList.remove(searchButtonShowClass);

        navItems?.classList.add(navHideClass);
        navItems?.classList.remove(navShowClass);

        searchButton.setAttribute("aria-expanded", "true");

        document.addEventListener("keydown", handleKeydown);

        const input = searchContainer.querySelector("input");
        if (input) input.focus();
    };

    const closeSearch = () => {
        isSearchOpen = false;

        searchContainer.classList.add(searchHideClass);
        searchContainer.classList.remove(searchShowClass);
        searchContainer.setAttribute("aria-hidden", "true");

        searchButton.classList.add(searchButtonShowClass);
        searchButton.classList.remove(searchButtonHideClass);

        navItems?.classList.remove(navHideClass);
        navItems?.classList.add(navShowClass);

        document.removeEventListener("keydown", handleKeydown);

        searchButton.setAttribute("aria-expanded", "false");
        searchButton.focus();
    };

    searchButton.addEventListener("click", openSearch);
    closeButton.addEventListener("click", closeSearch);

    return { openSearch, closeSearch, isSearchOpen };
};
